import { WalletContextState } from '@solana/wallet-adapter-react';
import type { useTRPC } from '../hooks/useTRPC';
import { extractErrorInfo } from './errorInfo';
import { isLeftResult } from '../lib/trpcTypeGuards';
import { normalizeSignedMessageResult } from './wallet';
import nacl from 'tweetnacl';

interface Attestation {
  slot_number: number;
  blockhash: string;
  request_unique_id: string;
}

interface AttestationResult {
  attestationPayload: string;
  requestUniqueId: string;
  attestationData: Attestation;
}

type TRPCClient = ReturnType<typeof useTRPC>;

const WALLET_SIGNING_ERROR_MESSAGE =
  'Connected wallet does not support Solana message signing for store attestations. Use a wallet that supports message signing and try again.';
const INVALID_SIGNATURE_FORMAT_MESSAGE =
  'Connected wallet returned an unsupported message signature format for the attestation. Reconnect and try again, or use a different wallet.';
const INVALID_SIGNATURE_VERIFICATION_MESSAGE =
  'Connected wallet returned an invalid Solana message signature for store attestation. Use a wallet that supports Solana message signing and try again.';

export function getAttestationErrorMessage(error: unknown): string {
  const extracted = extractErrorInfo(error, {
    defaultMessage: 'Failed to generate attestation',
  });
  const searchText = extracted.searchText;

  if (searchText.includes('wallet not connected')) {
    return 'Please connect a wallet that supports message signing.';
  }

  if (
    searchText.includes('walletsignmessageerror') ||
    searchText.includes('sign message') ||
    searchText.includes('signmessage')
  ) {
    return WALLET_SIGNING_ERROR_MESSAGE;
  }

  if (searchText.includes('invalid signature length')) {
    return INVALID_SIGNATURE_FORMAT_MESSAGE;
  }

  if (searchText.includes('invalid solana message signature')) {
    return INVALID_SIGNATURE_VERIFICATION_MESSAGE;
  }

  return extracted.message;
}

export async function createAttestationPayload(
  trpc: TRPCClient,
  wallet: WalletContextState,
): Promise<AttestationResult> {
  if (!trpc) {
    throw new Error('tRPC client is not available');
  }

  if (!wallet.publicKey || !wallet.signMessage) {
    throw new Error('Wallet not connected or does not support message signing');
  }

  const REQUEST_UNIQUE_ID_LEN = 32;
  const REQUEST_UNIQUE_ID_CHAR_SET = '0123456789';

  const requestUniqueId = Array(REQUEST_UNIQUE_ID_LEN)
    .fill(undefined)
    .map(() =>
      REQUEST_UNIQUE_ID_CHAR_SET.charAt(
        Math.floor(Math.random() * REQUEST_UNIQUE_ID_CHAR_SET.length),
      ),
    )
    .join('');

  const blockResult = await trpc.attestation.getBlockData.query({});
  if (isLeftResult(blockResult)) {
    throw new Error(
      blockResult.left.message || 'Failed to fetch attestation block data',
    );
  }

  const targetSlot = blockResult.right.slot;
  const blockhash = blockResult.right.blockhash;

  console.log('Attestation blockhash data:', {
    targetSlot,
    blockhash,
    commitment: 'finalized',
  });

  const attestation: Attestation = {
    slot_number: targetSlot,
    blockhash,
    request_unique_id: requestUniqueId,
  };

  const attestationString = JSON.stringify(attestation);
  const attestationBuffer = new TextEncoder().encode(attestationString);

  const signature = normalizeSignedMessageResult(
    await wallet.signMessage(attestationBuffer),
  );

  if (signature.length !== 64) {
    throw new Error(
      `Invalid signature length: expected 64, got ${signature.length}`,
    );
  }

  if (
    !nacl.sign.detached.verify(
      attestationBuffer,
      signature,
      wallet.publicKey.toBytes(),
    )
  ) {
    throw new Error(INVALID_SIGNATURE_VERIFICATION_MESSAGE);
  }

  // Combine signature + message to match nacl.sign format:
  // [64-byte signature][original message].
  const signedMessage = new Uint8Array(64 + attestationBuffer.length);
  signedMessage.set(signature, 0);
  signedMessage.set(attestationBuffer, 64);

  const attestationPayload = Buffer.from(signedMessage).toString('base64');

  console.log('Attestation details:', {
    attestation,
    signatureLength: signature.length,
    messageLength: attestationBuffer.length,
    totalLength: signedMessage.length,
    payloadLength: attestationPayload.length,
    walletPublicKey: wallet.publicKey.toBase58(),
  });

  return {
    attestationPayload,
    requestUniqueId,
    attestationData: attestation,
  };
}
